import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Video, Square, Play } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import io from 'socket.io-client';
import { SOCKET_PATH } from '../../utils/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WORKLET_PROCESSOR_CODE = `
const TARGET_SAMPLE_RATE = 16000;
const FRAME_SAMPLES = 640;

class PCM16DownsamplerProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._inputRate = sampleRate;
    this._ratio = this._inputRate / TARGET_SAMPLE_RATE;
    this._resampleAcc = 0;
    this._outBuffer = new Int16Array(FRAME_SAMPLES);
    this._outIdx = 0;
    this._muted = false;

    this.port.onmessage = (e) => {
      const msg = e.data || {};
      if (msg.type === 'mute') this._muted = !!msg.value;
    };
    this.port.postMessage({ type: 'ready', inputRate: this._inputRate });
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;
    const channel = input[0];
    if (!channel || channel.length === 0) return true;

    for (let i = 0; i < channel.length; i++) {
      this._resampleAcc += 1;
      while (this._resampleAcc >= this._ratio) {
        this._resampleAcc -= this._ratio;

        let s = this._muted ? 0 : channel[i];
        if (s > 1) s = 1; else if (s < -1) s = -1;
        this._outBuffer[this._outIdx++] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7FFF);

        if (this._outIdx >= FRAME_SAMPLES) {
          const out = new Int16Array(FRAME_SAMPLES);
          out.set(this._outBuffer);
          this._outIdx = 0;
          this.port.postMessage({ type: 'pcm', payload: out.buffer }, [out.buffer]);
        }
      }
    }
    return true;
  }
}
registerProcessor('pcm16-downsampler', PCM16DownsamplerProcessor);
`;

export const InterviewRoom = () => {
  const { interviewId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [interviewCompleted, setInterviewCompleted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const socketRef = useRef(null);

  const audioContextRef = useRef(null);
  const captureContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const workletNodeRef = useRef(null);
  const sinkNodeRef = useRef(null);
  const analyserRef = useRef(null);

  const recordingMixDestRef = useRef(null);
  const userMicMixSrcRef = useRef(null);
  const geminiPlaybackGainRef = useRef(null);

  const isStoppingRef = useRef(false);
  const isGraphBuiltRef = useRef(false);
  const isModelGeneratingRef = useRef(true);

  const nextPlayTimeRef = useRef(0);
  const isSpeakingRef = useRef(false);
  const speechStartRef = useRef(0);
  const silenceStartRef = useRef(Date.now());

  const sessionIdRef = useRef(null);
  const isRecordingRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.userType !== 'candidate') navigate('/interview-portal');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    return () => {
      cleanupAudioGraph();
    };
  }, []);

  const cleanupAudioGraph = () => {
    isStoppingRef.current = true;
    isGraphBuiltRef.current = false;
    isModelGeneratingRef.current = false;
    isRecordingRef.current = false;

    if (socketRef.current) {
      try { socketRef.current.disconnect(); } catch {}
      socketRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
    }
    try { sourceNodeRef.current?.disconnect(); } catch {}
    try { workletNodeRef.current?.disconnect(); } catch {}
    try { sinkNodeRef.current?.disconnect(); } catch {}
    try { analyserRef.current?.disconnect(); } catch {}
    try { userMicMixSrcRef.current?.disconnect(); } catch {}
    try { recordingMixDestRef.current?.disconnect(); } catch {}
    try { geminiPlaybackGainRef.current?.disconnect(); } catch {}

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
    if (captureContextRef.current && captureContextRef.current.state !== 'closed') {
      captureContextRef.current.close().catch(() => {});
    }
  };

  const requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: { 
          echoCancellation: { ideal: true }, 
          noiseSuppression: { ideal: true }, 
          autoGainControl: false 
        },
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setPermissionGranted(true);
      toast.success('Camera and microphone connected.');
    } catch (error) {
      toast.error('Failed to access camera/microphone');
    }
  };

  const startInterview = async () => {
    const videoTrack = mediaStreamRef.current?.getVideoTracks()[0];
    const audioTrack = mediaStreamRef.current?.getAudioTracks()[0];

    if (!videoTrack || videoTrack.readyState === 'ended' || !audioTrack || audioTrack.readyState === 'ended') {
      setPermissionGranted(false);
      toast.error('Media access was revoked! Please re-grant camera and microphone permissions.');
      return;
    }

    if (!permissionGranted || isRecording) return;

    try {
      setIsRecording(true);
      isRecordingRef.current = true;
      setShowStartDialog(false);

      isStoppingRef.current = false;
      isGraphBuiltRef.current = false;
      isModelGeneratingRef.current = true;

      const outCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (outCtx.state === 'suspended') await outCtx.resume();
      audioContextRef.current = outCtx;
      nextPlayTimeRef.current = outCtx.currentTime;

      const mixDest = outCtx.createMediaStreamDestination();
      recordingMixDestRef.current = mixDest;

      const userMicMixSrc = outCtx.createMediaStreamSource(mediaStreamRef.current);

      const candidateVoiceGain = outCtx.createGain();
      candidateVoiceGain.gain.value = 1.6;

      const compressor = outCtx.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-24, outCtx.currentTime);
      compressor.knee.setValueAtTime(30, outCtx.currentTime);
      compressor.ratio.setValueAtTime(12, outCtx.currentTime);
      compressor.attack.setValueAtTime(0.003, outCtx.currentTime);
      compressor.release.setValueAtTime(0.25, outCtx.currentTime);

      userMicMixSrc.connect(candidateVoiceGain);
      candidateVoiceGain.connect(compressor);
      compressor.connect(mixDest);

      userMicMixSrcRef.current = userMicMixSrc;

      const geminiVoiceNode = outCtx.createGain();
      geminiVoiceNode.connect(mixDest); 
      geminiVoiceNode.connect(outCtx.destination); 
      geminiPlaybackGainRef.current = geminiVoiceNode;

      const capCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
      captureContextRef.current = capCtx;

      const response = await api.post('/candidate/sessions/start', null, { params: { interview_id: interviewId } });
      const sid = response.data.session_id;
      setSessionId(sid);
      sessionIdRef.current = sid;

      const socket = io(BACKEND_URL, { path: SOCKET_PATH, transports: ['websocket'] });
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('join_interview', { session_id: sid, token: localStorage.getItem('token') });
      });

      socket.on('error', (err) => {
        console.error('💥 [Socket Error Block]:', err);
        toast.error(err.message || 'An error occurred in the live interview stream.');
      });

      socket.on('interview_ready', () => {
        console.log('🔗 [Socket Layer]: Room joined. Priming audio stream instantly.');
        if (!isGraphBuiltRef.current) {
          isGraphBuiltRef.current = true;
          startRecording(sid, captureContextRef.current).catch((e) => console.error('startRecording failed:', e));
        }
      });

      socket.on('gemini_transcription', (data) => {
        console.log(`🧠 [Gemini Heard]: "${data.text}"`);
      });

      socket.on('gemini_generating', () => {
        if (!isModelGeneratingRef.current) {
          isModelGeneratingRef.current = true;
        }
      });

      socket.on('gemini_speaking', () => {
        workletNodeRef.current?.port.postMessage({ type: 'mute', value: true });
        isModelGeneratingRef.current = true;
        isSpeakingRef.current = false; 
      });

      socket.on('gemini_listening', () => {
        isModelGeneratingRef.current = false;
        isSpeakingRef.current = false;
        silenceStartRef.current = Date.now();

        setTimeout(() => {
          if (!isModelGeneratingRef.current && isRecordingRef.current) {
            workletNodeRef.current?.port.postMessage({ type: 'mute', value: false });
            console.log("🎤 [VAD Gate]: Speakers flushed. Microphone is now live for candidate.");
          }
        }, 300);
      });

      socket.on('gemini_response', (data) => {
        if (data.audio) playAudioStreamFragment(data.audio);
      });

    } catch (error) {
      toast.error('Failed to start interview');
      setIsRecording(false);
      isRecordingRef.current = false;
    }
  };

  const startRecording = async (sid, captureContext) => {
    if (!mediaStreamRef.current || !socketRef.current) return;

    try {
      if (captureContext.state === 'suspended') await captureContext.resume();

      const blob = new Blob([WORKLET_PROCESSOR_CODE], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      await captureContext.audioWorklet.addModule(workletUrl);
      URL.revokeObjectURL(workletUrl);

      const source = captureContext.createMediaStreamSource(mediaStreamRef.current);
      const workletNode = new AudioWorkletNode(captureContext, 'pcm16-downsampler', {
        numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1,
      });
      const mutedGain = captureContext.createGain();
      mutedGain.gain.value = 0;

      workletNode.port.postMessage({ type: 'mute', value: true });

      const analyser = captureContext.createAnalyser();
      analyser.fftSize = 512;
      analyserRef.current = analyser;

      source.connect(analyser);
      source.connect(workletNode);
      workletNode.connect(mutedGain);
      mutedGain.connect(captureContext.destination);

      sourceNodeRef.current = source;
      workletNodeRef.current = workletNode;
      sinkNodeRef.current = mutedGain;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const checkAudioLevel = () => {
        if (isStoppingRef.current || !socketRef.current?.connected) return;

        if (isModelGeneratingRef.current) {
          requestAnimationFrame(checkAudioLevel);
          return;
        }

        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        let average = sum / dataArray.length;

        const THRESHOLD = 30;
        const SILENCE_DELAY = 1200;
        const MIN_SPEECH_DURATION = 300;

        if (average > THRESHOLD) {
          if (!isSpeakingRef.current) {
            isSpeakingRef.current = true;
            speechStartRef.current = Date.now();
          }
          silenceStartRef.current = Date.now();
        } else {
          const now = Date.now();
          const speechDuration = now - speechStartRef.current;
          
          if (isSpeakingRef.current && speechDuration > MIN_SPEECH_DURATION && (now - silenceStartRef.current > SILENCE_DELAY)) {
            isSpeakingRef.current = false;
            socketRef.current.emit('vad_turn_complete');
            console.log("🎤 [VAD Gate]: Candidate finished speaking. Yielding floor to Gemini.");
          }
        }
        requestAnimationFrame(checkAudioLevel);
      };

      checkAudioLevel();

      let logCounter = 0;

      workletNode.port.onmessage = (e) => {
        const msg = e.data;
        if (!msg || msg.type !== 'pcm' || isStoppingRef.current || !socketRef.current?.connected) return;

        if (isModelGeneratingRef.current) return;

        const bytes = new Uint8Array(msg.payload);
        let binary = '';
        const CHUNK = 8192;
        for (let j = 0; j < bytes.length; j += CHUNK) {
          binary += String.fromCharCode.apply(null, bytes.subarray(j, j + CHUNK));
        }
        const safeBase64 = window.btoa(binary);

        if (logCounter % 50 === 0) {
          console.log(`🟢 [Pipeline Status]: Streaming mic frames...`);
        }
        logCounter++;

        socketRef.current.emit('send_audio', { audio: safeBase64 });
      };

      const videoTrack = mediaStreamRef.current.getVideoTracks()[0];
      const mixedAudioTrack = recordingMixDestRef.current.stream.getAudioTracks()[0];
      const recordStream = new MediaStream([videoTrack, mixedAudioTrack]);

      const mediaRecorder = new MediaRecorder(recordStream, { mimeType: 'video/webm;codecs=vp8,opus' });
      
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data && event.data.size > 0) {
          const currentSessionId = sessionIdRef.current;
          if (!currentSessionId) return;

          const formData = new FormData();
          formData.append('file', event.data, `slice_${Date.now()}.webm`);
          
          try {
            await api.post('/candidate/sessions/' + currentSessionId + '/upload-chunk', formData);
            console.log("📤 [Video Sync]: 5-second slice uploaded successfully.");
          } catch (err) {
            console.error("Failed uploading continuous video segment slice:", err);
          }
        }
      };

      mediaRecorder.start(5000);
      mediaRecorderRef.current = mediaRecorder;

    } catch (err) {
      console.error('AudioWorklet graph compilation failed:', err);
    }
  };

  // 🔊 CRACKLE-FREE AUDIO SCHEDULER & GAIN ENVELOPE RAMPING
  const playAudioStreamFragment = (base64Audio) => {
    try {
      const audioCtx = audioContextRef.current;
      if (!audioCtx || !geminiPlaybackGainRef.current) return;
      if (audioCtx.state === 'suspended') audioCtx.resume();

      const binaryString = window.atob(base64Audio);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);

      const int16Samples = new Int16Array(bytes.buffer);
      const float32Samples = new Float32Array(int16Samples.length);
      for (let i = 0; i < int16Samples.length; i++) float32Samples[i] = int16Samples[i] / 32768.0;

      const audioBuffer = audioCtx.createBuffer(1, float32Samples.length, 24000);
      audioBuffer.getChannelData(0).set(float32Samples);

      // Create local gain node for chunk envelope smoothing (prevents DC clicks/pops)
      const chunkGainNode = audioCtx.createGain();
      const bufferSource = audioCtx.createBufferSource();
      bufferSource.buffer = audioBuffer;

      bufferSource.connect(chunkGainNode);
      chunkGainNode.connect(geminiPlaybackGainRef.current);

      // 120ms Lookahead Jitter Buffer absorbs connection latency spikes
      const currentTime = audioCtx.currentTime;
      if (nextPlayTimeRef.current < currentTime) {
        nextPlayTimeRef.current = currentTime + 0.12; 
      }

      const startTime = nextPlayTimeRef.current;
      const endTime = startTime + audioBuffer.duration;

      // 5ms Linear Fade-In and Fade-Out Ramping eliminates border crackling
      const fadeDuration = 0.005;
      chunkGainNode.gain.setValueAtTime(0.001, startTime);
      chunkGainNode.gain.linearRampToValueAtTime(1.0, startTime + fadeDuration);
      chunkGainNode.gain.setValueAtTime(1.0, endTime - fadeDuration);
      chunkGainNode.gain.linearRampToValueAtTime(0.001, endTime);

      bufferSource.start(startTime);
      nextPlayTimeRef.current += audioBuffer.duration;
    } catch (error) {
      console.error('Playback failed:', error);
    }
  };

  const stopInterview = async () => {
    if (isUploading) return;

    isStoppingRef.current = true;
    isRecordingRef.current = false;
    isGraphBuiltRef.current = false;
    setIsUploading(true);
    toast.info("Saving interview... Please don't close the tab.");

    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      await new Promise((resolve) => setTimeout(resolve, 1200));
      cleanupAudioGraph();

      if (sessionId) {
        await api.post('/candidate/sessions/' + sessionId + '/complete');
      }

      setIsRecording(false);
      setInterviewCompleted(true);
      setShowStopDialog(false);
      toast.success('Interview completed successfully!');

      setTimeout(() => navigate('/interview-portal/dashboard'), 2000);
    } catch (error) {
      toast.error('Failed to complete interview session.');
      setIsUploading(false);
      setIsRecording(false);
    }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-900"><p className="text-slate-300">Loading...</p></div>;
  if (!user || user.userType !== 'candidate') return null;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6">
      <div className="max-w-4xl w-full space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-light text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AI Interview Session
          </h1>
        </div>

        <div className="bg-slate-800 rounded-md border border-slate-700 p-1 aspect-video">
          <video ref={videoRef} autoPlay muted playsInline className="w-full h-full rounded object-cover" />
        </div>

        <div className="flex justify-center gap-4">
          {!permissionGranted ? (
            <Button onClick={requestPermissions} size="lg">
              <Video className="w-5 h-5 mr-2" />
              Grant Camera & Mic Access
            </Button>
          ) : !isRecording && !interviewCompleted ? (
            <Button onClick={() => setShowStartDialog(true)} size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Play className="w-5 h-5 mr-2" />
              Start Interview
            </Button>
          ) : isRecording ? (
            <Button onClick={() => setShowStopDialog(true)} size="lg" variant="destructive" disabled={isUploading}>
              <Square className="w-5 h-5 mr-2" />
              {isUploading ? 'Saving...' : 'Stop Interview'}
            </Button>
          ) : null}
        </div>
      </div>

      <AlertDialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start Interview?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>The AI interviewer will begin asking questions immediately.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={startInterview}>Start</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop Interview?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>Are you sure you want to stop?</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={stopInterview}>Stop</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};